import {
  createContext,
  useContext,
  useEffect,
  useReducer,
  useRef,
  useState,
} from "react";
// utils
import Cookies from "universal-cookie";
// contexts
import { useAuth } from "@/contexts/AuthContext";
import MerchantContext from "@/contexts/MerchantContext";

import { getShopId } from "@/utils/util";
import { useLocalStorage } from "@/hooks/useLocalStorage";
import { useLoadScript } from "@react-google-maps/api";
import { googleMapAPILibraries, OrderModalStateActions } from "@/enums/enums";

const OrderContext = createContext();

const OrderContextProvider = ({ children }) => {
  const merchantInfoContext = useContext(MerchantContext);
  const cookie = new Cookies();
  const { isAuthenticated } = useAuth();
  const shopId = getShopId();
  const [orderType, setOrderType] = useState("");
  const [tableNumber, setTableNumber] = useState("");
  const { isLoaded: googleMapsIsLoaded, loadError } = useLoadScript({
    googleMapsApiKey: process.env.GOOGLE_MAPS_API_KEY,
    libraries: googleMapAPILibraries,
  });
  //store address store for guest, local storage to persist data between pages
  const [localStorageAddress, setLocalStorageAddress] = useLocalStorage(
    `${getShopId()}@storeAddress`,
    {}
  );
  const [deliveryAddress, setDeliveryAddress] = useState({
    name: "",
    selectedLatLng: { lat: null, lng: null },
    address: "",
    city: "",
    country: "",
    postalCode: "",
    state: "",
  });
  // const [orderModalState, setOrderModalState] = useState({
  //   view: false,
  //   validateOnOpen: false,
  //   section: null,
  // });

  const INITIAL_ORDER_MODAL_STATE = {
    view: false,
    validateOnOpen: false,
    section: null,
    addressDetailToEdit: null,
    closeAfterSubmitting: false,
  };

  const orderModalReducer = (state, action) => {
    switch (action.type) {
      case OrderModalStateActions.ADD_ADDRESS:
        return {
          ...INITIAL_ORDER_MODAL_STATE,
          view: true,
          section: "Add",
        };
      case OrderModalStateActions.EDIT_ADDRESS:
        return {
          ...INITIAL_ORDER_MODAL_STATE,
          view: true,
          section: isAuthenticated ? "Edit" : null,
          addressDetailToEdit: action.data.addressDetailToEdit,
          ...(action.data.closeAfterSubmitting && {
            closeAfterSubmitting: action.data.closeAfterSubmitting,
          }),
        };
      case OrderModalStateActions.CLOSE:
        return {
          ...INITIAL_ORDER_MODAL_STATE,
          view: false,
        };
      case OrderModalStateActions.HOME:
        return {
          ...INITIAL_ORDER_MODAL_STATE,
          view: true,
          section: null,
        };
      case OrderModalStateActions.OPEN_VALIDATE:
        return {
          ...INITIAL_ORDER_MODAL_STATE,
          view: true,
          validateOnOpen: true,
          section: null,
        };
      default:
        throw new Error("Invalid reducer action type for orderModalReducer");
    }
  };

  const [orderModalState, dispatchOrderModalState] = useReducer(
    orderModalReducer,
    INITIAL_ORDER_MODAL_STATE
  );

  const [deliveryAddressList, setDeliveryAddressList] = useState([]);

  const signInData = cookie.get(`${getShopId()}@signIn`);

  useEffect(() => {
    if (!isAuthenticated)
      localStorage.removeItem(`${getShopId()}@storeAddress`);
  }, [isAuthenticated]);

  let orderTypeInitialized = useRef(false);

  //using useEffect to initialized orderType cookie as previously ssr pages will render page incorrectly.
  useEffect(() => {
    if (
      !orderTypeInitialized.current &&
      merchantInfoContext.orderOption.length > 0
    ) {
      //temporararily hardcode as delivery for AC
      setOrderType(
        getShopId() === "fam"
          ? "Delivery"
          : cookie.get(`${shopId}@orderType`)
          ? cookie.get(`${shopId}@orderType`)
          : merchantInfoContext.orderOption[0]
      );
      orderTypeInitialized = true;
    }
  }, [cookie, merchantInfoContext.orderOption]);

  const handleChangeOrderType = (orderTypeInput) => {
    setOrderType(orderTypeInput);
    cookie.set(`${shopId}@orderType`, orderTypeInput, { path: "/" });
  };

  return (
    <OrderContext.Provider
      value={{
        orderType,
        handleChangeOrderType,
        tableNumber,
        setTableNumber,
        deliveryAddress: signInData ? deliveryAddress : localStorageAddress,
        setDeliveryAddress: signInData
          ? setDeliveryAddress
          : setLocalStorageAddress,
        deliveryAddressList,
        setDeliveryAddressList,
        orderModalState,
        dispatchOrderModalState,
        googleMapsIsLoaded,
      }}
    >
      {children}
    </OrderContext.Provider>
  );
};
const useOrder = () => {
  return useContext(OrderContext);
};
export { OrderContext, OrderContextProvider, useOrder };
